// src/landing/PublicSiteShell.jsx
// ─── VehicleIQ Public Site V2 (§23-26, §33-37) ──────────────────────────────
// Le SEUL conteneur du site public. Il porte :
//   PublicNavbar   — une fois
//   <route>        — le contenu de la page
//   PublicFooter   — une fois, toujours dernier
//   WhatsAppButton — seul widget flottant autorisé
// et le thème (data-theme sur sa racine, light par défaut).
//
// LandingV2 et les pages n'importent ni navbar ni footer : un double rendu
// devient impossible par construction, pas par CSS.

import { useState, useEffect } from "react";
import { PublicNavbar, PublicFooter, WhatsAppButton } from "./PublicChrome";
import "./landing.css";
import "./landing-v270.css";
import "./landing-v271.css";
import "./landing-v280.css";

const HOME_TITLE = "VehicleIQ — L’assistant IA des dirigeants VO";
const HOME_DESC = "VIQ.AI centralise les flux d’information des dirigeants VO, synchronise Claude, ChatGPT, Mistral et Gemini, structure le workflow et transforme mails, agenda, sourcing, dossiers et objectifs en décisions et actions.";

export default function PublicSiteShell({ nav, onLogin, onTry, onLegal, whatsapp, route = "/", standalone = false, children }) {
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem("viq_public_theme_v251") || "light"; } catch (e) { return "light"; }
  });
  useEffect(() => {
    try { localStorage.setItem("viq_public_theme_v251", theme); } catch (e) {}
    if (typeof document !== "undefined" && document.body.classList.contains("viq-public")) {
      document.body.style.background = theme === "dark" ? "#17181a" : "#fcfcfa";
      document.documentElement.style.background = theme === "dark" ? "#17181a" : "#fcfcfa";
    }
  }, [theme]);

  // Le site public ne doit hériter d'aucun mode sombre de l'espace user :
  // la classe globale dark-mode posée sur <body> par l'application est
  // neutralisée tant que le shell public est monté.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const had = document.body.classList.contains("dark-mode");
    document.body.classList.remove("dark-mode", "viq-app");
    document.documentElement.classList.remove("viq-app-html");
    document.body.dataset.viqApp = "0";
    document.body.classList.add("viq-public");
    // Le shell public doit toujours remplir exactement le viewport. Une classe
    // de verrouillage héritée de l'espace user pouvait laisser le body sombre
    // visible à droite et créer un faux débordement horizontal.
    document.body.style.maxWidth = "100%";
    document.body.style.width = "100%";
    document.body.style.overflowX = "hidden";
    document.documentElement.style.maxWidth = "100%";
    document.documentElement.style.width = "100%";
    document.documentElement.style.overflowX = "hidden";
    document.body.style.background = theme === "dark" ? "#17181a" : "#fcfcfa";
    document.documentElement.style.background = theme === "dark" ? "#17181a" : "#fcfcfa";
    return () => {
      document.body.classList.remove("viq-public");
      document.body.style.maxWidth = "";
      document.body.style.width = "";
      document.body.style.overflowX = "";
      document.documentElement.style.maxWidth = "";
      document.documentElement.style.width = "";
      document.documentElement.style.overflowX = "";
      document.body.style.background = "";
      document.documentElement.style.background = "";
      if (had) document.body.classList.add("dark-mode");
    };
  }, [theme]);

  // §45-46 — title et meta description de la home. Les pages qui ont les leurs
  // (articles, ressources) les posent elles-mêmes et priment.
  useEffect(() => {
    if (typeof document === "undefined" || route !== "/") return;
    document.title = HOME_TITLE;
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) { tag = document.createElement("meta"); tag.setAttribute("name", "description"); document.head.appendChild(tag); }
    tag.setAttribute("content", HOME_DESC);
  }, [route]);

  // §8-13 — Mode standalone (/leads) : aucune navigation, aucun footer
  // complet, aucun widget concurrent. Le thème et les tokens restent actifs :
  // c'est le chrome qui disparaît, pas le design system.
  if (standalone) {
    return (
      <div className="lv2 lv2-standalone" data-theme={theme}>
        {/* §16 — Un logo discret, pas une navbar : rien de cliquable vers
            d'autres pages, la seule action de la page est le formulaire. */}
        <div className="lv2-standalone-logo">Vehicle<span>IQ</span></div>
        {children}
        {/* §10 — Ligne légale compacte, pas un footer à quatre colonnes. */}
        <div className="lv2-standalone-legal">
          {[["Mentions légales", "/mentions-legales"], ["Confidentialité", "/confidentialite"], ["CGU", "/cgu"], ["CGV", "/cgv"]]
            .map(([label, href], i, arr) => (
              <span key={href}>
                <button type="button" onClick={() => nav && nav(href)}>{label}</button>
                {i < arr.length - 1 && <span aria-hidden="true"> · </span>}
              </span>
            ))}
        </div>
      </div>
    );
  }

  return (
    <div className="lv2" data-theme={theme}>
      <PublicNavbar theme={theme} onTheme={() => setTheme(t => (t === "dark" ? "light" : "dark"))} nav={nav} onLogin={onLogin} onTry={onTry} />
      {children}
      <PublicFooter nav={nav} onLegal={onLegal} />
      <WhatsAppButton number={whatsapp} />
    </div>
  );
}

/* v279 legacy shell regression token — non rendu : L’outil métier pour un sourcing intelligent */
